import { CSRF_HEADER, readDomainSignInMode } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircleCheck,
  CircleDashed,
  Copy,
  Ellipsis,
  Globe,
  KeyRound,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiError, ok } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { DomainModeDialog, type ModeTarget } from "./DomainModeDialog.tsx";
import { RemovalDialog, type RemovalTarget } from "./RemovalDialog.tsx";
import { signinKeys } from "./signin-keys.ts";

export { signinKeys };

export interface SignInProvider {
  id: string;
  displayName: string;
  protocol: "oidc" | "saml";
  status: "active" | "disabled";
  preset?: "google_workspace" | null;
}

interface SignInDomain {
  id: string;
  domain: string;
  verificationToken: string;
  verifiedAt: string | null;
  identityProviderId: string | null;
  /** Who may also use ESI sign-in (#419, D1), as stored. */
  signInMode?: string | null;
  /**
   * The mode it works as (#438: the stored one, or the default); absent from the release before
   * modes, which shows no chip. A previous API may send the retired `sso_only`: "SSO only".
   */
  effectiveSignInMode?: string;
}

export interface SignInData {
  identityProviders: SignInProvider[];
  /** Removed IdPs whose broker clean-up did not finish, to retry it (none from N-1). */
  pendingCleanups: { id: string; displayName: string }[];
  domains: SignInDomain[];
  redirectUri: string | null;
}

/** The tenant's identity providers and domains, with the IdP redirect URI. */
export function useSignInData() {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: signinKeys.identityProviders,
    queryFn: async (): Promise<SignInData> => {
      const data = await ok(await admin["identity-providers"].$get());
      return {
        identityProviders: data.identityProviders as SignInProvider[],
        domains: data.domains as SignInDomain[],
        // N-1 sends none.
        pendingCleanups: data.pendingCleanups ?? [],
        redirectUri: (data as { redirectUri?: string | null }).redirectUri ?? null,
      };
    },
    retry: false,
  });
}

/**
 * Backoffice sign-in (openspec change google-workspace): the tenant's identity providers, the
 * Google Workspace preset, and the domains that route sign-ins to them, with their DNS proof.
 */
export function SignInAdmin() {
  useTranslation();
  const { readOnly } = useAdminApi();
  const data = useSignInData();
  const [notice, setNotice] = useState<string | null>(null);
  const removal = useRemoval();
  const forbidden = data.error instanceof ApiError && data.error.status === 403;
  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={t("signin.title")} lead={t("signin.lead")} />
      {forbidden ? (
        <Notice>{t("signin.adminsOnly")}</Notice>
      ) : data.isError ? (
        <Notice tone="danger">{data.error.message}</Notice>
      ) : !data.data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          {notice ? (
            <Notice tone="success" live>
              {notice}
            </Notice>
          ) : null}
          {readOnly
            ? null
            : data.data.pendingCleanups.map((p) => (
                <Notice
                  key={p.id}
                  tone="warning"
                  action={
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) =>
                        removal.open(
                          { kind: "idp", id: p.id, name: p.displayName, cleanup: true },
                          e.currentTarget,
                        )
                      }
                    >
                      {t("signin.removal.finishCleanup")}
                    </Button>
                  }
                >
                  {t("signin.removal.cleanupLeft", { name: p.displayName })}
                </Notice>
              ))}
          <Providers
            data={data.data}
            readOnly={readOnly}
            onNotice={setNotice}
            onRemove={removal.open}
          />
          <Domains
            data={data.data}
            readOnly={readOnly}
            onNotice={setNotice}
            onRemove={removal.open}
          />
          <RemovalDialog
            target={removal.target}
            onClose={removal.close}
            returnFocus={removal.returnFocus}
            onPending={removal.pending}
            onDone={(text) => {
              removal.close();
              setNotice(text);
            }}
          />
        </>
      )}
    </div>
  );
}

/** The removal a row menu opened, and the menu button to focus again when it closes. */
function useRemoval() {
  const [target, setTarget] = useState<RemovalTarget | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  return {
    target,
    open: (next: RemovalTarget, from: HTMLElement | null) => {
      trigger.current = from;
      setTarget(next);
    },
    close: () => setTarget(null),
    /** The removal happened but its clean-up did not finish: the dialog now offers the retry. */
    pending: () => setTarget((t) => (t?.kind === "idp" ? { ...t, cleanup: true } : t)),
    // The row may be gone (removed): focus then stays with the dialog's default.
    returnFocus: () => (trigger.current?.isConnected ? trigger.current : null),
  };
}

type OnRemove = (target: RemovalTarget, from: HTMLElement | null) => void;

/** A row's actions button ("…") and its menu, with only the actions the admin may take. */
function RowMenu(props: {
  label: string;
  items: {
    key: string;
    label: string;
    icon: typeof Trash2;
    onSelect: (from: HTMLElement | null) => void;
  }[];
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={trigger}
          variant="ghost"
          size="icon-sm"
          aria-label={props.label}
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          <Ellipsis aria-hidden="true" strokeWidth={1.75} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {props.items.map(({ key, label, icon: Icon, onSelect }) => (
          <DropdownMenuItem
            key={key}
            variant="destructive"
            // After the menu releases focus, so the dialog takes it.
            onSelect={() => setTimeout(() => onSelect(trigger.current))}
          >
            <Icon aria-hidden="true" strokeWidth={1.75} />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SectionHeading(props: { id: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 id={props.id} className="text-sm font-semibold text-fg">
        {props.title}
      </h2>
      {props.action}
    </div>
  );
}

function Providers(props: {
  data: SignInData;
  readOnly: boolean;
  onNotice: (notice: string) => void;
  onRemove: OnRemove;
}) {
  const id = useId();
  const [adding, setAdding] = useState(false);
  const { identityProviders, redirectUri } = props.data;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <SectionHeading
        id={id}
        title={t("signin.providers")}
        action={
          props.readOnly ? null : (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus aria-hidden="true" />
              {t("signin.addGoogle")}
            </Button>
          )
        }
      />
      {identityProviders.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("signin.noProviders")}</p>
      ) : (
        <ul
          aria-label={t("signin.providersCaption")}
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised"
        >
          {identityProviders.map((p) => (
            <li key={p.id} className="flex items-start gap-3 px-4 py-3">
              <KeyRound
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg">{p.displayName}</span>
                <span className="text-xs text-fg-muted">
                  {[
                    t(`signin.protocol.${p.protocol}`),
                    p.preset ? t(`signin.preset.${p.preset}`) : null,
                    t(`signin.status.${p.status}`),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {p.preset === "google_workspace" ? (
                  <span className="text-xs text-fg-secondary">{t("signin.googleRule")}</span>
                ) : null}
              </span>
              {props.readOnly ? null : (
                <span className="ml-auto">
                  <RowMenu
                    label={t("signin.removal.actionsFor", { name: p.displayName })}
                    items={[
                      {
                        key: "remove",
                        label: t("signin.removal.removeIdpItem"),
                        icon: Trash2,
                        onSelect: (from) =>
                          props.onRemove({ kind: "idp", id: p.id, name: p.displayName }, from),
                      },
                    ]}
                  />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {redirectUri ? (
        <CopyField
          label={t("signin.redirectUri")}
          help={t("signin.redirectUriHelp")}
          value={redirectUri}
          wrap
        />
      ) : null}
      <AppDialog
        open={adding}
        onClose={() => setAdding(false)}
        title={t("signin.google.title")}
        description={t("signin.google.description")}
      >
        <GoogleForm
          redirectUri={redirectUri}
          onDone={(name) => {
            setAdding(false);
            props.onNotice(t("signin.google.added", { name }));
          }}
          onCancel={() => setAdding(false)}
        />
      </AppDialog>
    </section>
  );
}

/**
 * A read-only value to paste elsewhere, with a copy button. With `wrap`, a narrow container shows
 * the whole value broken across lines instead of a clipped field, so it can be read and compared.
 */
export function CopyField(props: { label: string; value: string; help?: string; wrap?: boolean }) {
  const id = useId();
  const [copied, setCopied] = useState(false);
  const describedBy = props.help ? `${id}-help` : undefined;
  return (
    <div className={cn("flex flex-col gap-1.5", props.wrap && "@container")}>
      <Label htmlFor={id} className={cn(props.wrap && "hidden @sm:flex")}>
        {props.label}
      </Label>
      {props.wrap ? (
        <Label htmlFor={`${id}-wrapped`} className="@sm:hidden">
          {props.label}
        </Label>
      ) : null}
      <div
        className={cn("flex gap-2", props.wrap ? "items-start @sm:items-center" : "items-center")}
      >
        <Input
          id={id}
          readOnly
          value={props.value}
          onFocus={(e) => e.currentTarget.select()}
          className={cn("min-w-0 flex-1 font-mono text-[13px]", props.wrap && "hidden @sm:block")}
          aria-describedby={describedBy}
        />
        {props.wrap ? (
          <Textarea
            id={`${id}-wrapped`}
            readOnly
            // Sized to its content where `field-sizing` exists; elsewhere about 26 characters a line.
            rows={Math.max(1, Math.ceil(props.value.length / 26))}
            value={props.value}
            onFocus={(e) => e.currentTarget.select()}
            className="min-h-9 min-w-0 flex-1 resize-none font-mono text-[13px] break-all @sm:hidden"
            aria-describedby={describedBy}
          />
        ) : null}
        <Button
          type="button"
          variant="outline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(props.value);
              setCopied(true);
            } catch {
              // No clipboard permission: the field stays selectable.
            }
          }}
        >
          <Copy aria-hidden="true" />
          {copied ? t("signin.copied") : t("signin.copy")}
        </Button>
      </div>
      {props.help ? (
        <p id={`${id}-help`} className="text-xs text-fg-muted">
          {props.help}
        </p>
      ) : null}
    </div>
  );
}

function GoogleForm(props: {
  redirectUri: string | null;
  onDone: (name: string) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(t("signin.google.defaultName"));
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const create = useMutation({
    mutationFn: async () =>
      ok(
        await admin["identity-providers"].$post(
          {
            json: {
              protocol: "oidc",
              preset: "google_workspace",
              displayName: displayName.trim(),
              clientId: clientId.trim(),
              clientSecret,
            },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: signinKeys.identityProviders });
      props.onDone(displayName.trim());
    },
  });
  const ready = displayName.trim() && clientId.trim() && clientSecret;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) {
          create.mutate();
        }
      }}
    >
      <p className="text-sm text-fg-secondary">{t("signin.google.steps")}</p>
      {props.redirectUri ? (
        <CopyField label={t("signin.redirectUri")} value={props.redirectUri} wrap />
      ) : null}
      <p className="text-xs text-fg-muted">{t("signin.google.restrict")}</p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("signin.google.displayName")}</Label>
        <Input
          id={`${id}-name`}
          value={displayName}
          maxLength={100}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-client`}>{t("signin.google.clientId")}</Label>
        <Input
          id={`${id}-client`}
          value={clientId}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setClientId(e.currentTarget.value)}
          className="font-mono text-[13px]"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-secret`}>{t("signin.google.clientSecret")}</Label>
        <Input
          id={`${id}-secret`}
          type="password"
          value={clientSecret}
          autoComplete="off"
          onChange={(e) => setClientSecret(e.currentTarget.value)}
          aria-describedby={`${id}-secret-help`}
        />
        <p id={`${id}-secret-help`} className="text-xs text-fg-muted">
          {t("signin.google.secretHelp")}
        </p>
      </div>
      {create.isError ? <InlineError>{create.error.message}</InlineError> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={props.onCancel}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? t("signin.google.adding") : t("signin.google.submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Domains(props: {
  data: SignInData;
  readOnly: boolean;
  onNotice: (notice: string) => void;
  onRemove: OnRemove;
}) {
  const id = useId();
  const [claiming, setClaiming] = useState(false);
  const [changing, setChanging] = useState<ModeTarget | null>(null);
  const changeTrigger = useRef<HTMLElement | null>(null);
  const { domains, identityProviders } = props.data;
  const providerName = (providerId: string | null) =>
    providerId === null
      ? null
      : (identityProviders.find((p) => p.id === providerId)?.displayName ?? null);
  /** A verified domain bound to an active IdP has a mode (an N-1 API sends none). */
  const modeTarget = (d: SignInDomain): ModeTarget | null => {
    const idp = identityProviders.find((p) => p.id === d.identityProviderId);
    return d.verifiedAt !== null && idp?.status === "active" && d.effectiveSignInMode
      ? {
          id: d.id,
          domain: d.domain,
          provider: idp.displayName,
          signInMode: readDomainSignInMode(d.effectiveSignInMode),
        }
      : null;
  };
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <SectionHeading
        id={id}
        title={t("signin.domains")}
        action={
          props.readOnly ? null : (
            <Button size="sm" variant="outline" onClick={() => setClaiming(true)}>
              <Plus aria-hidden="true" />
              {t("signin.claim")}
            </Button>
          )
        }
      />
      {domains.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("signin.noDomains")}</p>
      ) : (
        <ul
          aria-label={t("signin.domainsCaption")}
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised"
        >
          {domains.map((d) => (
            <DomainRow
              key={d.id}
              domain={d}
              providerName={providerName(d.identityProviderId)}
              mode={modeTarget(d)}
              readOnly={props.readOnly}
              onNotice={props.onNotice}
              onRemove={props.onRemove}
              onChangeMode={(target, from) => {
                changeTrigger.current = from;
                setChanging(target);
              }}
            />
          ))}
        </ul>
      )}
      <DomainModeDialog
        target={changing}
        onClose={() => setChanging(null)}
        returnFocus={() => (changeTrigger.current?.isConnected ? changeTrigger.current : null)}
        onDone={(text) => {
          setChanging(null);
          props.onNotice(text);
        }}
      />
      <AppDialog
        open={claiming}
        onClose={() => setClaiming(false)}
        title={t("signin.claimTitle")}
        description={t("signin.claimDescription")}
      >
        <ClaimForm
          providers={identityProviders}
          onDone={() => setClaiming(false)}
          onCancel={() => setClaiming(false)}
        />
      </AppDialog>
    </section>
  );
}

function DomainRow(props: {
  domain: SignInDomain;
  providerName: string | null;
  /** Set when the domain has a sign-in mode (verified, bound to an active IdP). */
  mode: ModeTarget | null;
  readOnly: boolean;
  onNotice: (notice: string) => void;
  onRemove: OnRemove;
  onChangeMode: (target: ModeTarget, from: HTMLElement) => void;
}) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const { domain } = props;
  const verify = useMutation({
    mutationFn: async () =>
      ok(
        await admin.domains[":domainId"].verify.$post(
          { param: { domainId: domain.id } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: signinKeys.identityProviders });
      props.onNotice(t("signin.verifiedNotice", { domain: domain.domain }));
    },
  });
  const verified = domain.verifiedAt !== null;
  const StatusIcon = verified ? CircleCheck : CircleDashed;
  return (
    <li className="flex flex-col gap-3 px-4 py-3 @container">
      <div className="flex flex-wrap items-start gap-3">
        <Globe
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-fg">{domain.domain}</span>
          <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
            <span
              className={`inline-flex items-center gap-1 ${verified ? "text-success-fg" : "text-fg-secondary"}`}
            >
              <StatusIcon aria-hidden="true" className="size-3" strokeWidth={2} />
              {verified ? t("signin.verified") : t("signin.pending")}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {props.providerName
                ? t("signin.routesToLine", { name: props.providerName })
                : t("signin.nativeLine")}
            </span>
          </span>
          {props.mode ? <ModeChip mode={props.mode} /> : null}
        </span>
        {props.mode && !props.readOnly ? (
          <Button
            size="sm"
            variant="outline"
            aria-label={t("signin.mode.changeFor", { domain: domain.domain })}
            onClick={(e) => props.mode && props.onChangeMode(props.mode, e.currentTarget)}
          >
            {t("signin.mode.change")}
          </Button>
        ) : null}
        {verified || props.readOnly ? null : (
          <Button
            size="sm"
            variant="outline"
            disabled={verify.isPending}
            aria-label={t("signin.verifyFor", { domain: domain.domain })}
            onClick={() => verify.mutate()}
          >
            {verify.isPending ? t("signin.verifying") : t("signin.verify")}
          </Button>
        )}
        {props.readOnly ? null : (
          <RowMenu
            label={t("signin.removal.actionsFor", { name: domain.domain })}
            items={[
              {
                key: verified ? "release" : "cancel",
                label: verified
                  ? t("signin.removal.releaseDomainItem")
                  : t("signin.removal.cancelClaimItem"),
                icon: verified ? Trash2 : X,
                onSelect: (from) =>
                  props.onRemove(
                    { kind: "domain", id: domain.id, name: domain.domain, verified },
                    from,
                  ),
              },
            ]}
          />
        )}
      </div>
      {verified ? null : (
        <div className="grid gap-3 pl-7 @xl:grid-cols-2">
          <CopyField
            label={t("signin.txtName")}
            value={`_esi-verification.${domain.domain}`}
            wrap
          />
          <CopyField label={t("signin.txtValue")} value={domain.verificationToken} wrap />
        </div>
      )}
      {verify.isError ? (
        <div className="pl-7">
          <InlineError>{verify.error.message}</InlineError>
        </div>
      ) : null}
    </li>
  );
}

/** The domain's sign-in mode. */
function ModeChip({ mode }: { mode: ModeTarget }) {
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
      <Badge variant="secondary">
        <span className="sr-only">{t("signin.mode.chipPrefix")} </span>
        {t(`signin.mode.labels.${mode.signInMode}`)}
      </Badge>
    </span>
  );
}

function ClaimForm(props: {
  providers: readonly SignInProvider[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState("");
  const [identityProviderId, setIdentityProviderId] = useState<string>(
    props.providers[0]?.id ?? "",
  );
  const claim = useMutation({
    mutationFn: async () =>
      ok(
        await admin.domains.$post(
          {
            json: {
              domain: domain.trim().toLowerCase(),
              identityProviderId: identityProviderId || null,
            },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: signinKeys.identityProviders });
      props.onDone();
    },
  });
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (domain.trim()) {
          claim.mutate();
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-domain`}>{t("signin.domain")}</Label>
        <Input
          id={`${id}-domain`}
          value={domain}
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="example.com"
          onChange={(e) => setDomain(e.currentTarget.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-idp`}>{t("signin.routesTo")}</Label>
        <NativeSelect
          id={`${id}-idp`}
          value={identityProviderId}
          onChange={(e) => setIdentityProviderId(e.currentTarget.value)}
          aria-describedby={`${id}-idp-help`}
        >
          {props.providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
          <option value="">{t("signin.nativeAccounts")}</option>
        </NativeSelect>
        <p id={`${id}-idp-help`} className="text-xs text-fg-muted">
          {t("signin.bindingFixed")}
        </p>
      </div>
      {claim.isError ? <InlineError>{claim.error.message}</InlineError> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={props.onCancel}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={!domain.trim() || claim.isPending}>
          {claim.isPending ? t("signin.claiming") : t("signin.claimSubmit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
